<script setup lang="ts">
import { computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n } from '@/i18n'
import type { ShapeLayer, ShapeGeometry } from '@/types/document'
import SliderField from '@/components/common/SliderField.vue'

const props = defineProps<{ layer: ShapeLayer }>()
const docStore = useDocumentStore()
const { t } = useI18n()
const g = computed(() => props.layer.geometry)
function setGeom(patch: Record<string, number>, commit = true) {
  set({ geometry: { ...g.value, ...patch } as ShapeGeometry }, commit)
}
let inTx = false
function set(patch: Parameters<typeof docStore.setShape>[1], commit = true) {
  if (!commit && !inTx) {
    docStore.beginTransaction('history.editShape')
    inTx = true
  }
  docStore.setShape(props.layer.id, patch)
  if (commit && inTx) {
    docStore.commitTransaction()
    inTx = false
  }
}
</script>

<template>
  <div class="sc">
    <label class="row"><input type="checkbox" :checked="layer.fillHex !== null" @change="set({ fillHex: ($event.target as HTMLInputElement).checked ? '#4f9cf5' : null })" /><span>{{ t('shape.fill') }}</span>
      <input v-if="layer.fillHex" type="color" :value="layer.fillHex" @input="set({ fillHex: ($event.target as HTMLInputElement).value }, false)" @change="set({ fillHex: ($event.target as HTMLInputElement).value })" />
    </label>
    <SliderField v-if="layer.fillHex" :label="t('layer.opacity')" :value="layer.fillAlpha * 100" :min="0" :max="100" unit="%" @input="v => set({ fillAlpha: v / 100 }, false)" @change="v => set({ fillAlpha: v / 100 })" />
    <label class="row"><input type="checkbox" :checked="layer.strokeHex !== null" @change="set({ strokeHex: ($event.target as HTMLInputElement).checked ? '#000000' : null })" /><span>{{ t('shape.stroke') }}</span>
      <input v-if="layer.strokeHex" type="color" :value="layer.strokeHex" @input="set({ strokeHex: ($event.target as HTMLInputElement).value }, false)" @change="set({ strokeHex: ($event.target as HTMLInputElement).value })" />
    </label>
    <template v-if="layer.strokeHex">
      <SliderField :label="t('shape.strokeWidth')" :value="layer.strokeWidth" :min="0" :max="100" unit="px" @input="v => set({ strokeWidth: v }, false)" @change="v => set({ strokeWidth: v })" />
      <label class="row"><span class="muted">{{ t('shape.strokeAlign') }}</span>
        <select :value="layer.strokeAlign" @change="set({ strokeAlign: ($event.target as HTMLSelectElement).value as 'center' })">
          <option value="center">{{ t('dialog.style.center') }}</option><option value="inside">{{ t('dialog.style.inside') }}</option><option value="outside">{{ t('dialog.style.outside') }}</option>
        </select>
      </label>
    </template>
    <SliderField v-if="g.type === 'rect'" :label="t('shape.radius')" :value="g.radius" :min="0" :max="Math.max(1, Math.min(g.w, g.h) / 2)" unit="px" @input="v => setGeom({ radius: v }, false)" @change="v => setGeom({ radius: v })" />
    <template v-if="g.type === 'polygon'">
      <SliderField :label="t('shape.sides')" :value="g.sides" :min="3" :max="24" @input="v => setGeom({ sides: v }, false)" @change="v => setGeom({ sides: v })" />
      <SliderField :label="t('shape.star')" :value="g.star * 100" :min="0" :max="95" unit="%" @input="v => setGeom({ star: v / 100 }, false)" @change="v => setGeom({ star: v / 100 })" />
    </template>
  </div>
</template>

<style scoped>
.sc {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
